import { useState, useRef, useEffect, ReactNode } from 'react'
import { router, usePage } from '@inertiajs/react'
import { Key, Mail, Link as LinkIcon, X, Copy, Check, Send } from 'lucide-react'
import { PageProps } from '@/types'

// The four admin-initiated password endpoints. Defaults to the agent-scoped
// routes; the Users page passes the /admin/users equivalents so accounts with
// no agent record get the same controls.
export interface PasswordEndpoints {
  setPassword: string
  resetLink: string
  sendResetEmail: string
  sendWelcomeEmail: string
}

interface Props {
  agentId: number
  agentName: string
  sendgridConnected: boolean
  variant?: 'menu' | 'buttons'
  trigger?: ReactNode
  endpoints?: PasswordEndpoints
  /** Top-level params key wrapping the password fields. */
  passwordParamKey?: string
}

export function agentPasswordEndpoints(agentId: number): PasswordEndpoints {
  return {
    setPassword: `/agents/${agentId}/password`,
    resetLink: `/agents/${agentId}/password/reset_link`,
    sendResetEmail: `/agents/${agentId}/password/send_reset_email`,
    sendWelcomeEmail: `/agents/${agentId}/password/send_welcome_email`,
  }
}

type ModalKind = null | 'set' | 'link'

function getCsrfToken(): string {
  const el = document.querySelector('meta[name="csrf-token"]')
  return el?.getAttribute('content') || ''
}

export default function AgentPasswordActions({
  agentId,
  agentName,
  sendgridConnected,
  variant = 'menu',
  trigger,
  endpoints,
  passwordParamKey = 'agent_password',
}: Props) {
  const urls = endpoints ?? agentPasswordEndpoints(agentId)
  const [open, setOpen] = useState(false)
  const [modal, setModal] = useState<ModalKind>(null)
  const [linkUrl, setLinkUrl] = useState<string | null>(null)
  const [linkLoading, setLinkLoading] = useState(false)
  const [emailLoading, setEmailLoading] = useState(false)
  const [welcomeLoading, setWelcomeLoading] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const { props: pageProps } = usePage<PageProps>()
  const csrfFromProps = (pageProps as { csrf_token?: string }).csrf_token

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  function handleSendEmail(e?: React.MouseEvent) {
    e?.stopPropagation()
    if (!sendgridConnected || emailLoading) return
    setOpen(false)
    setEmailLoading(true)
    router.post(
      urls.sendResetEmail,
      {},
      {
        preserveScroll: true,
        onFinish: () => setEmailLoading(false),
      }
    )
  }

  function handleSendWelcome(e?: React.MouseEvent) {
    e?.stopPropagation()
    if (!sendgridConnected || welcomeLoading) return
    const ok = confirm(
      `Re-send the welcome email to ${agentName}?\n\nThis generates a NEW temporary password and replaces their current password. They will need the new password from the email to sign in.`
    )
    if (!ok) return
    setOpen(false)
    setWelcomeLoading(true)
    router.post(
      urls.sendWelcomeEmail,
      {},
      {
        preserveScroll: true,
        onFinish: () => setWelcomeLoading(false),
      }
    )
  }

  async function handleCopyLink(e?: React.MouseEvent) {
    e?.stopPropagation()
    setOpen(false)
    setModal('link')
    setLinkUrl(null)
    setLinkCopied(false)
    setLinkLoading(true)
    try {
      const res = await fetch(urls.resetLink, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfFromProps || getCsrfToken(),
        },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setLinkUrl(data.url)
    } catch (err) {
      setLinkUrl('')
      alert('Could not generate reset link. Please try again.')
      setModal(null)
    } finally {
      setLinkLoading(false)
    }
  }

  async function copyToClipboard() {
    if (!linkUrl) return
    try {
      await navigator.clipboard.writeText(linkUrl)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch {
      // ignore
    }
  }

  function openSetPassword(e?: React.MouseEvent) {
    e?.stopPropagation()
    setOpen(false)
    setModal('set')
  }

  const sendEmailDisabled = !sendgridConnected || emailLoading
  const sendEmailTooltip = sendgridConnected
    ? 'Email a password reset link to the agent'
    : 'Connect SendGrid under Settings → Integrations to send password reset emails.'
  const welcomeDisabled = !sendgridConnected || welcomeLoading
  const welcomeTooltip = sendgridConnected
    ? 'Send the welcome email again with a new temporary password (replaces their current password)'
    : 'Connect SendGrid under Settings → Integrations to send welcome emails.'

  if (variant === 'buttons') {
    return (
      <>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openSetPassword}
            className="btn-secondary flex items-center gap-2"
            title="Set a new password for this agent"
          >
            <Key className="h-4 w-4" />
            Set password
          </button>
          <button
            type="button"
            onClick={handleCopyLink}
            className="btn-secondary flex items-center gap-2"
            title="Generate a one-time reset link to share with the agent"
          >
            <LinkIcon className="h-4 w-4" />
            Copy reset link
          </button>
          <button
            type="button"
            onClick={handleSendEmail}
            disabled={sendEmailDisabled}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title={sendEmailTooltip}
          >
            <Mail className="h-4 w-4" />
            {emailLoading ? 'Sending…' : 'Send reset email'}
          </button>
          <button
            type="button"
            onClick={handleSendWelcome}
            disabled={welcomeDisabled}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title={welcomeTooltip}
          >
            <Send className="h-4 w-4" />
            {welcomeLoading ? 'Sending…' : 'Re-send welcome email'}
          </button>
        </div>
        {renderModals()}
      </>
    )
  }

  return (
    <>
      <div className="relative inline-block" ref={menuRef}>
        <div onClick={(e) => { e.stopPropagation(); setOpen((o) => !o) }}>
          {trigger || (
            <button
              type="button"
              className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest"
              title="Password actions"
            >
              <Key className="h-4 w-4" />
            </button>
          )}
        </div>
        {open && (
          <div
            className="absolute right-0 mt-1 w-56 bg-white border border-chestnut-charcoal/10 rounded-lg shadow-lg z-20 py-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={openSetPassword}
              className="w-full text-left px-3 py-2 text-sm text-chestnut-charcoal hover:bg-chestnut-cream flex items-center gap-2"
            >
              <Key className="h-4 w-4" /> Set password
            </button>
            <button
              type="button"
              onClick={handleCopyLink}
              className="w-full text-left px-3 py-2 text-sm text-chestnut-charcoal hover:bg-chestnut-cream flex items-center gap-2"
            >
              <LinkIcon className="h-4 w-4" /> Copy reset link
            </button>
            <button
              type="button"
              onClick={handleSendEmail}
              disabled={sendEmailDisabled}
              title={sendEmailTooltip}
              className="w-full text-left px-3 py-2 text-sm text-chestnut-charcoal hover:bg-chestnut-cream flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Mail className="h-4 w-4" />
              {emailLoading ? 'Sending…' : 'Send reset email'}
            </button>
            <button
              type="button"
              onClick={handleSendWelcome}
              disabled={welcomeDisabled}
              title={welcomeTooltip}
              className="w-full text-left px-3 py-2 text-sm text-chestnut-charcoal hover:bg-chestnut-cream flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-4 w-4" />
              {welcomeLoading ? 'Sending…' : 'Re-send welcome email'}
            </button>
          </div>
        )}
      </div>
      {renderModals()}
    </>
  )

  function renderModals() {
    return (
      <>
        {modal === 'set' && (
          <SetPasswordModal
            endpoint={urls.setPassword}
            paramKey={passwordParamKey}
            agentName={agentName}
            onClose={() => setModal(null)}
          />
        )}
        {modal === 'link' && (
          <ResetLinkModal
            url={linkUrl}
            loading={linkLoading}
            copied={linkCopied}
            agentName={agentName}
            onCopy={copyToClipboard}
            onClose={() => setModal(null)}
          />
        )}
      </>
    )
  }
}

function SetPasswordModal({
  endpoint,
  paramKey,
  agentName,
  onClose,
}: {
  endpoint: string
  paramKey: string
  agentName: string
  onClose: () => void
}) {
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirmation) {
      setError('Password and confirmation do not match.')
      return
    }
    setSubmitting(true)
    router.post(
      endpoint,
      { [paramKey]: { password, password_confirmation: confirmation } },
      {
        preserveScroll: true,
        onFinish: () => setSubmitting(false),
        onSuccess: () => onClose(),
      }
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-chestnut-charcoal/60 hover:text-chestnut-charcoal"
        >
          <X className="h-5 w-5" />
        </button>
        <h3 className="text-lg font-semibold text-chestnut-charcoal mb-1">Set password</h3>
        <p className="text-sm text-chestnut-charcoal/60 mb-4">
          Choose a new password for <strong>{agentName}</strong>. They can sign in with it immediately.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-chestnut-charcoal mb-1">New password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field w-full"
              autoFocus
              minLength={8}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Confirm password</label>
            <input
              type="password"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              className="input-field w-full"
              minLength={8}
              required
            />
          </div>
          {error && <p className="text-sm text-status-error">{error}</p>}
          <p className="text-xs text-chestnut-charcoal/60">Minimum 8 characters.</p>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? 'Saving…' : 'Save password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ResetLinkModal({
  url,
  loading,
  copied,
  agentName,
  onCopy,
  onClose,
}: {
  url: string | null
  loading: boolean
  copied: boolean
  agentName: string
  onCopy: () => void
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-chestnut-charcoal/60 hover:text-chestnut-charcoal"
        >
          <X className="h-5 w-5" />
        </button>
        <h3 className="text-lg font-semibold text-chestnut-charcoal mb-1">Password reset link</h3>
        <p className="text-sm text-chestnut-charcoal/60 mb-4">
          Share this one-time link with <strong>{agentName}</strong>. It opens the
          standard reset-password form.
        </p>
        {loading || !url ? (
          <p className="text-sm text-chestnut-charcoal/60">Generating link…</p>
        ) : (
          <>
            <div className="flex items-stretch gap-2">
              <input
                type="text"
                value={url}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
                className="input-field flex-1 font-mono text-xs"
              />
              <button type="button" onClick={onCopy} className="btn-primary flex items-center gap-2">
                {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}
              </button>
            </div>
            <p className="text-xs text-chestnut-charcoal/60 mt-3">
              Link expires after the standard reset window (typically 6 hours).
            </p>
          </>
        )}
        <div className="flex justify-end pt-4">
          <button type="button" onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
    </div>
  )
}
